/**
 * React hooks for RSVPs
 * Integrates with TanStack Query for caching and state management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as rsvpsService from '../services/rsvps';
import type { EventRsvpUpdate } from '../types/database';
import type { EventRsvpWithProfile } from '../types/rsvps.types';
import { eventKeys } from './useEvents';
import { notificationKeys } from './useNotificationPreferences';

// Query keys for consistent cache management
export const rsvpKeys = {
  all: ['rsvps'] as const,
  lists: () => [...rsvpKeys.all, 'list'] as const,
  byEvent: (eventId: number) =>
    [...rsvpKeys.lists(), 'event', eventId] as const,
  byUser: (userId: string) => [...rsvpKeys.lists(), 'user', userId] as const,
  byUserForEvents: (userId: string, eventIds: number[]) =>
    [
      ...rsvpKeys.all,
      'userEvents',
      userId,
      eventIds.sort((a, b) => a - b),
    ] as const,
  detail: (eventId: number, userId: string) =>
    [...rsvpKeys.all, 'detail', eventId, userId] as const,
};

/**
 * Fetch all RSVPs for an event
 */
export function useEventRsvps(eventId: number) {
  return useQuery<EventRsvpWithProfile[]>({
    queryKey: rsvpKeys.byEvent(eventId),
    queryFn: () => rsvpsService.getEventRsvps(eventId),
    enabled: !!eventId,
  });
}

/**
 * Fetch RSVP for a specific user and event
 */
export function useUserRsvp(eventId: number, userId: string) {
  return useQuery({
    queryKey: rsvpKeys.detail(eventId, userId),
    queryFn: () => rsvpsService.getUserRsvp(eventId, userId),
    enabled: !!eventId && !!userId,
  });
}

/**
 * Fetch RSVPs for a user across multiple events in a single batch query.
 * Returns a Map of eventId -> EventRsvp for efficient lookup.
 * This reduces N+1 queries to a single query when displaying event lists.
 */
export function useUserRsvpsForEvents(
  eventIds: number[],
  userId: string | null
) {
  return useQuery({
    queryKey: rsvpKeys.byUserForEvents(userId || '', eventIds),
    queryFn: () => rsvpsService.getUserRsvpsForEvents(eventIds, userId || ''),
    enabled: eventIds.length > 0 && !!userId,
  });
}

/**
 * Create or update an RSVP
 */
export function useUpsertRsvp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rsvpsService.upsertRsvp,
    onSuccess: (result) => {
      const userId = result.rsvp.user_id;

      // Invalidate cache for all events that received RSVPs
      for (const rsvp of result.allRsvps) {
        queryClient.invalidateQueries({
          queryKey: rsvpKeys.byEvent(rsvp.event_id),
        });
        queryClient.setQueryData(
          rsvpKeys.detail(rsvp.event_id, rsvp.user_id),
          rsvp
        );
        queryClient.invalidateQueries({
          queryKey: notificationKeys.eventSubscriptionRoot(),
        });
      }

      // Invalidate user-specific queries (only need to do once per user)
      queryClient.invalidateQueries({
        queryKey: rsvpKeys.byUser(userId),
      });
      // Invalidate batch queries for this user
      queryClient.invalidateQueries({
        queryKey: [...rsvpKeys.all, 'userEvents', userId],
      });

      // Invalidate "Going" events queries so the Events page updates immediately
      queryClient.invalidateQueries({ queryKey: eventKeys.going(userId) });
      queryClient.invalidateQueries({
        queryKey: eventKeys.goingInfinite(userId),
      });
    },
  });
}

/**
 * Update an existing RSVP
 */
export function useUpdateRsvp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: EventRsvpUpdate }) =>
      rsvpsService.updateRsvp(id, updates),
    onSuccess: (data) => {
      const userId = data.user_id;

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: rsvpKeys.byEvent(data.event_id),
      });
      queryClient.invalidateQueries({
        queryKey: rsvpKeys.byUser(userId),
      });
      // Invalidate batch queries for this user
      queryClient.invalidateQueries({
        queryKey: [...rsvpKeys.all, 'userEvents', userId],
      });
      queryClient.setQueryData(rsvpKeys.detail(data.event_id, userId), data);

      // Invalidate "Going" events queries so the Events page updates immediately
      queryClient.invalidateQueries({ queryKey: eventKeys.going(userId) });
      queryClient.invalidateQueries({
        queryKey: eventKeys.goingInfinite(userId),
      });
    },
  });
}

/**
 * Delete an RSVP
 */
export function useDeleteRsvp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rsvpsService.deleteRsvp,
    onSuccess: (deletedData) => {
      // Invalidate specific queries based on the deleted RSVP
      if (deletedData) {
        const userId = deletedData.user_id;

        queryClient.invalidateQueries({
          queryKey: rsvpKeys.byEvent(deletedData.event_id),
        });
        queryClient.invalidateQueries({
          queryKey: rsvpKeys.byUser(userId),
        });
        // Invalidate batch queries for this user
        queryClient.invalidateQueries({
          queryKey: [...rsvpKeys.all, 'userEvents', userId],
        });
        // Remove the specific detail query (set to null since it's deleted)
        queryClient.setQueryData(
          rsvpKeys.detail(deletedData.event_id, userId),
          null
        );

        // Invalidate "Going" events queries so the Events page updates immediately
        queryClient.invalidateQueries({ queryKey: eventKeys.going(userId) });
        queryClient.invalidateQueries({
          queryKey: eventKeys.goingInfinite(userId),
        });
      }
      // Also invalidate all lists as a fallback
      queryClient.invalidateQueries({ queryKey: rsvpKeys.lists() });
    },
  });
}
